'use client';

/**
 * Standalone responsive image with optional caption + click-through.
 * Ported from ThemeMaker's {@code blocks/media/Image}.
 *
 * <p>Distinct from inline images in a {@link RichText} (which the
 * rich-text editor handles). Use this for hero-sized standalone
 * visuals where you want explicit aspect-ratio + alignment control,
 * and where editors want to swap the image without scrubbing
 * through a contenteditable HTML blob.
 *
 * <p>Falls back to a dashed placeholder card when {@code src} is empty
 * — gives editors a visible "set me" affordance in the preview.
 */
import type { CSSProperties, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { tx } from '../_shared/coerce';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

const widthMap: Record<string, string> = {
  narrow: '480px',
  default: '880px',
  wide: '1100px',
  full: '100%',
};

const aspectMap: Record<string, string> = {
  auto: 'auto',
  '16/9': '16 / 9',
  '4/3': '4 / 3',
  '1/1': '1 / 1',
  '3/2': '3 / 2',
  '21/9': '21 / 9',
};

export interface ImageProps {
  src?: string;
  alt?: string;
  caption?: Localized;
  href?: string;
  width?: keyof typeof widthMap;
  aspect?: keyof typeof aspectMap;
  align?: 'left' | 'center' | 'right';
  /** When true, image gets a soft hover lift on link variants. */
  interactive?: boolean;
}

export function Image({
  src,
  alt = '',
  caption,
  href,
  width = 'default',
  aspect = 'auto',
  align = 'center',
  interactive = true,
}: ImageProps) {
  const { t } = useTranslation('site-builder-blocks');
  const captionText = tx(caption);

  if (!src) {
    return (
      <BlockRoot
        as="div"
        style={{
          marginLeft: align === 'right' || align === 'center' ? 'auto' : 0,
          marginRight: align === 'left' || align === 'center' ? 'auto' : 0,
          maxWidth: widthMap[width] ?? widthMap['default'],
          aspectRatio: aspect === 'auto' ? '16 / 9' : aspectMap[aspect],
          background: 'var(--color-neutral-100, #f3f4f6)',
          border: '1px dashed var(--color-border-subtle, rgba(0,0,0,0.1))',
          borderRadius: 'var(--radius-md, 8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-tertiary, #6a6f73)',
          fontSize: 'var(--font-size-sm, 14px)',
          padding: 'var(--space-12, 48px)',
        }}
      >
        {t('imageBlock.noSource')}
      </BlockRoot>
    );
  }

  const imgStyle: CSSProperties = {
    display: 'block',
    width: '100%',
    height: aspect === 'auto' ? 'auto' : '100%',
    aspectRatio: aspect === 'auto' ? undefined : aspectMap[aspect],
    objectFit: aspect === 'auto' ? undefined : 'cover',
    borderRadius: 'var(--radius-md, 8px)',
  };

  const aspectHeights: Record<string, number> = {
    '16/9': 9,
    '4/3': 3,
    '1/1': 1,
    '3/2': 2,
    '21/9': 9,
  };
  const aspectWidths: Record<string, number> = {
    '16/9': 16,
    '4/3': 4,
    '1/1': 1,
    '3/2': 3,
    '21/9': 21,
  };
  const imgW = aspect !== 'auto' ? (aspectWidths[aspect] ?? 16) : undefined;
  const imgH = aspect !== 'auto' ? (aspectHeights[aspect] ?? 9) : undefined;
  const img = (
    <img
      src={src}
      alt={alt}
      style={imgStyle}
      loading="lazy"
      decoding="async"
      {...(imgW ? { width: imgW * 100, height: imgH! * 100 } : {})}
    />
  );

  const marginLeft = align === 'right' || align === 'center' ? 'auto' : 0;
  const marginRight = align === 'left' || align === 'center' ? 'auto' : 0;

  return (
    <BlockRoot
      as="figure"
      style={{
        margin: 0,
        maxWidth: widthMap[width] ?? widthMap['default'],
        marginLeft,
        marginRight,
      }}
    >
      {href ? (
        <a
          href={href}
          style={{
            display: 'block',
            textDecoration: 'none',
            transition: interactive ? 'transform 200ms ease' : undefined,
          }}
          onMouseEnter={
            interactive
              ? (e: MouseEvent<HTMLAnchorElement>) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              : undefined
          }
          onMouseLeave={
            interactive
              ? (e: MouseEvent<HTMLAnchorElement>) => {
                  e.currentTarget.style.transform = '';
                }
              : undefined
          }
        >
          {img}
        </a>
      ) : (
        img
      )}
      {captionText && (
        <figcaption
          style={{
            marginTop: 8,
            fontSize: '13px',
            color: 'var(--color-text-tertiary, #6a6f73)',
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {captionText}
        </figcaption>
      )}
    </BlockRoot>
  );
}
